
import axios from 'axios';

const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000;
const AUTH_TOKEN_KEY = process.env.REACT_APP_AUTH_TOKEN_KEY || 'token';
const API_URL = process.env.REACT_APP_API_BASE_URL;

const api = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.error('Authentication failed. Please log in again.');
      // Optionally redirect to login
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const orderAssignmentAPI = {
  // Get orders ready for delivery assignment
  getReadyForDelivery: () => {
    return api.get('/deliveryagent/orders/ready');
  },

  // Get active delivery agents (with optional role filter)
  getActiveAgents: (role = null) => {
    const params = role ? { role } : {};
    return api.get('/deliveryagent/available', { params });
  },

  // Assign agent to order
  assignAgent: (orderId, orderType, agentId,deliveryType = 'sample') => {
    return api.post('/deliveryagent/assign', {
      orderId,
      orderType,
      agentId,
      deliveryType 
    });
  },

  // Get assignment details (doesn't update)
  getAssignmentDetails: (token) => {
    return api.post('/deliveryagent/assignment/details', { token });
  },

  // Accept assignment (called after confirmation)
  acceptAssignment: (token) => {
    return api.post('/deliveryagent/assignment/accept', { token });
  },

  // Reject assignment
  rejectAssignment: (token, reason) => {
    return api.post('/deliveryagent/assignment/reject', {
      token,
      reason,
    });
  },

  // Get order status details (doesn't update)
  getOrderStatusDetails: (token) => {
    return api.post('/deliveryagent/order/status-details', { token });
  },

  // Confirm pickup (updates after confirmation)
  confirmPickup: (token) => {
    return api.post('/deliveryagent/order/pickup', { token });
  },

  // Confirm delivery (updates after confirmation)
  confirmDelivery: (token) => {
    return api.post('/deliveryagent/order/deliver', { token });
  },

  // Update order status (generic)
  updateStatus: (orderId, status, notes) => {
    return api.patch(`/deliveryagent/orders/${orderId}/status`, {
      status,
      notes,
    });
  },

  // Get delivery agent details
  getAgentDetails: (agentId) => {
    return api.get(`/deliveryagent/${agentId}`);
  },

  // Get agent's assigned orders
  getAgentOrders: (agentId) => {
    return api.get(`/deliveryagent/${agentId}/orders`);
  },
};

export const deliveryAgentAPI = {
  // Get all agents
  getAll: () => {
    return api.get('/deliveryagent/');
  },

  // Get single agent
  getAgent: (agentId) => {
    return api.get(`/deliveryagent/${agentId}`);
  },

  // Create new agent
  create: (agentData) => {
    return api.post('/deliveryagent/', agentData);
  },

  // Update agent
  update: (agentId, agentData) => {
    return api.put(`/deliveryagent/${agentId}`, agentData);
  },


  toggleStatus: (agentId) => {
    return api.put(`/deliveryagent/${agentId}/status`);
  },


  // Delete agent
  delete: (agentId) => {
    return api.delete(`/deliveryagent/${agentId}`);
  },

  // Get available agents 
  getAvailable: (role = null) => {
    const params = role ? { role } : {};
    return api.get('/deliveryagent/available', { params });
  },
};

export const ordersAPI = {
  // Get all orders
  getAll: () => {
    return api.get('/orders');
  },

  // Get single order
  getOrder: (orderId) => {
    return api.get(`/orders/${orderId}`);
  },

  // Update order
  update: (orderId, orderData) => {
    return api.put(`/orders/${orderId}`, orderData);
  },

  // Update order status
  updateStatus: (orderId, status) => {
    return api.patch(`/orders/${orderId}/status`, { status });
  },
};

export default api;